import React from "react";
import { Tag } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { getFormattedAmount, formatSmartPrice } from "Shared/Utils";
import { Tokens } from "Shared/Tokens";

export const formatQty = (val) => {
    const num = Number(val || 0);
    if (!Number.isFinite(num)) return "0";
    if (Math.abs(num) >= 1000) {
        return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
    }
    if (Math.abs(num) >= 1) {
        return num.toLocaleString("en-US", { maximumFractionDigits: 4 });
    }
    return num.toLocaleString("en-US", { maximumFractionDigits: 6 });
};

export const formatDateGroupKey = (ms, timezone = "Asia/Kolkata") => {
    const raw = Number(ms || 0);
    if (!raw || !Number.isFinite(raw)) return "Recorded Activity";
    const t = raw > 1e12 ? raw : raw * 1000;
    try {
        return new Date(t).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            ...(timezone && timezone !== "local" ? { timeZone: timezone } : {}),
        });
    } catch (e) {
        return new Date(t).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    }
};

export const formatTimeShort = (ms, timezone = "Asia/Kolkata") => {
    const raw = Number(ms || 0);
    if (!raw || !Number.isFinite(raw)) return "";
    const t = raw > 1e12 ? raw : raw * 1000;
    try {
        return new Date(t).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            ...(timezone && timezone !== "local" ? { timeZone: timezone } : {}),
        });
    } catch (e) {
        return new Date(t).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });
    }
};

export const getEventBadgeConfig = (ev) => {
    if (ev.origin === "manual" || ev.source === "manual") {
        const isSell = ev.type === "sell";
        return {
            label: isSell ? "MANUAL SELL" : "MANUAL BUY",
            bg: isSell ? Tokens.accentSubtle : Tokens.positiveBg,
            color: isSell ? Tokens.accent : Tokens.positive,
            border: isSell ? Tokens.accentBorder : Tokens.positiveBorder,
        };
    }
    const sourceNorm = String(ev.source || "").toLowerCase();
    if (sourceNorm === "auto-invest" || sourceNorm === "earn") {
        const isSell = ev.type === "sell";
        let earnLabel = "EARN BUY";
        if (sourceNorm === "auto-invest") {
            earnLabel = "AUTO-INVEST";
        } else if (isSell) {
            earnLabel = "EARN SELL";
        }
        return {
            label: earnLabel,
            bg: isSell ? Tokens.accentSubtle : Tokens.positiveBg,
            color: isSell ? Tokens.accent : Tokens.positive,
            border: isSell ? Tokens.accentBorder : Tokens.positiveBorder,
        };
    }
    if (sourceNorm === "convert") {
        return {
            label: ev.type === "sell" ? "CONVERT OUT" : "CONVERT IN",
            bg: Tokens.accentSubtle,
            color: Tokens.accent,
            border: Tokens.accentBorder,
        };
    }
    if (ev.type === "sell") {
        const prefix =
            sourceNorm && sourceNorm !== "spot"
                ? `${sourceNorm.toUpperCase()} `
                : "SPOT ";
        return {
            label: `${prefix}SELL`,
            bg: Tokens.accentSubtle,
            color: Tokens.accent,
            border: Tokens.accentBorder,
        };
    }
    if (ev.type === "deposit") {
        return {
            label: "DEPOSIT",
            bg: Tokens.surface,
            color: Tokens.textSecondary,
            border: Tokens.hairline,
        };
    }
    if (ev.type === "withdrawal") {
        return {
            label: "WITHDRAWAL",
            bg: Tokens.warningBg,
            color: Tokens.warning,
            border: Tokens.warningBorder,
        };
    }
    const prefix =
        sourceNorm && sourceNorm !== "spot"
            ? `${sourceNorm.toUpperCase()} `
            : "SPOT ";
    return {
        label: `${prefix}BUY`,
        bg: Tokens.positiveBg,
        color: Tokens.positive,
        border: Tokens.positiveBorder,
    };
};

const TradebookEventRow = ({
    ev,
    isLast = false,
    isLedgerMode = false,
    currencySymbol = "$",
    exchangeRate = 1,
    timezone = "Asia/Kolkata",
    showCoin = true,
    onSelect = null,
    onCopyQty = null,
    rowTestId = "ledger-timeline-row",
    runningBalanceTestId = "ledger-row-running-balance",
}) => {
    const rate =
        Number.isFinite(Number(exchangeRate)) && Number(exchangeRate) > 0
            ? Number(exchangeRate)
            : 1;
    const isManual = ev.origin === "manual" || ev.source === "manual";
    const mult = isManual ? 1 : rate;

    const money = (val) =>
        `${currencySymbol}${getFormattedAmount(
            Math.abs(Number(val || 0) * mult)
        )}`;

    const smartPrice = (val) =>
        `${currencySymbol}${formatSmartPrice(
            Math.abs(Number(val || 0) * mult)
        )}`;

    const usdtFmt = (val) =>
        `${getFormattedAmount(Math.abs(Number(val || 0)))} USDT`;

    const signed = (val) => (Number(val || 0) >= 0 ? "+" : "-");

    const badge = getEventBadgeConfig(ev);
    const isPositiveQty = ev.type === "buy" || ev.type === "deposit";
    const isSell = ev.type === "sell";
    const hasRealized =
        isSell || Math.abs(Number(ev.realizedPlDelta || 0)) > 1e-8;
    const realizedPos = Number(ev.realizedPlDelta || 0) >= 0;
    const runningBalValue =
        ev.runningTotalInvested !== undefined
            ? ev.runningTotalInvested
            : ev.afterCostBasis;
    const accountBalUsdt =
        ev.computedAccountUsdt !== undefined
            ? ev.computedAccountUsdt
            : runningBalValue;
    const timeShort = formatTimeShort(ev.time, timezone);

    let rightRunningLabel = `Cost: ${money(ev.afterCostBasis)} · Bal: ${money(
        runningBalValue
    )}`;
    if (isLedgerMode) {
        rightRunningLabel = `Bal: ${money(accountBalUsdt)} USDT`;
    } else if (ev.afterQty !== undefined && !showCoin) {
        rightRunningLabel = `Cost: ${money(
            ev.afterCostBasis
        )} · Bal: ${formatQty(ev.afterQty)} ${ev.coin}`;
    }

    return (
        <div
            role="button"
            tabIndex={0}
            data-testid={rowTestId}
            onClick={() => {
                if (typeof onSelect === "function") {
                    onSelect(ev);
                }
            }}
            onKeyPress={(e) => {
                if (e.key === "Enter" && typeof onSelect === "function") {
                    onSelect(ev);
                }
            }}
            style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                padding: "12px 14px",
                borderBottom: !isLast ? `1px solid ${Tokens.hairline}` : "none",
                cursor: onSelect ? "pointer" : "default",
            }}
        >
            {/* Line 1: Coin + Event Badge + Time on left, Dollar Amount + Realized P&L on right */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        minWidth: 0,
                        overflow: "hidden",
                    }}
                >
                    {showCoin && (
                        <span
                            style={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: Tokens.textPrimary,
                                flexShrink: 0,
                            }}
                        >
                            {ev.coin}
                        </span>
                    )}
                    <Tag
                        style={{
                            margin: 0,
                            backgroundColor: badge.bg,
                            color: badge.color,
                            border: `1px solid ${badge.border}`,
                            borderRadius: Tokens.radiusTag,
                            fontFamily: Tokens.fontMono,
                            fontSize: "10px",
                            padding: "0 5px",
                            lineHeight: "16px",
                            flexShrink: 0,
                        }}
                    >
                        {badge.label}
                    </Tag>
                    {timeShort && (
                        <span
                            style={{
                                fontSize: "11px",
                                color: Tokens.textMuted,
                                fontFamily: Tokens.fontMono,
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            {timeShort}
                        </span>
                    )}
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                        fontFamily: Tokens.fontMono,
                        fontVariantNumeric: "tabular-nums",
                        flexShrink: 0,
                    }}
                >
                    <span
                        style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: Tokens.textPrimary,
                        }}
                    >
                        {(() => {
                            if (!(ev.quoteQty > 0)) {
                                return "Transfer";
                            }
                            if (isLedgerMode) {
                                const cashSign =
                                    ev.type === "buy" ||
                                    ev.type === "withdrawal"
                                        ? "-"
                                        : "+";
                                return `${cashSign}${usdtFmt(ev.quoteQty)}`;
                            }
                            return money(ev.quoteQty);
                        })()}
                    </span>
                    {hasRealized && (
                        <span
                            style={{
                                fontSize: "11px",
                                fontWeight: 500,
                                color: realizedPos
                                    ? Tokens.positive
                                    : Tokens.negative,
                            }}
                        >
                            ({signed(ev.realizedPlDelta)}
                            {money(ev.realizedPlDelta)})
                        </span>
                    )}
                </div>
            </div>

            {/* Line 2: Account USDT Breakdown (Ledger) OR Coin Execution & Running Balance (Tradebook) */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    fontSize: "11px",
                    fontFamily: Tokens.fontMono,
                    fontVariantNumeric: "tabular-nums",
                }}
            >
                <div
                    style={{
                        color: Tokens.textSecondary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                    }}
                >
                    {isLedgerMode ? (
                        <span>
                            {ev.computedAfterCashUsdt > 0
                                ? `Cash: ${usdtFmt(
                                      ev.computedAfterCashUsdt
                                  )} · Crypto: ${usdtFmt(
                                      ev.computedInvestedUsdt
                                  )}`
                                : `Crypto: ${usdtFmt(
                                      ev.computedInvestedUsdt
                                  )} · ${isPositiveQty ? "+" : "-"}${formatQty(
                                      ev.qty
                                  )} ${ev.coin}`}
                        </span>
                    ) : (
                        <React.Fragment>
                            <span>
                                {isPositiveQty ? "+" : "-"}
                                {formatQty(ev.qty)} {ev.coin}
                                {ev.unitPrice > 0
                                    ? ` @ ${smartPrice(ev.unitPrice)}`
                                    : ""}
                                {ev.afterQty !== undefined
                                    ? ` · ${formatQty(ev.afterQty)} ${ev.coin}`
                                    : ""}
                            </span>
                            {onCopyQty && (
                                <CopyOutlined
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onCopyQty(ev.qty, "Quantity");
                                    }}
                                    style={{
                                        color: Tokens.textMuted,
                                        cursor: "pointer",
                                        fontSize: "11px",
                                        flexShrink: 0,
                                    }}
                                    title="Copy quantity"
                                />
                            )}
                        </React.Fragment>
                    )}
                </div>

                <div
                    data-testid={runningBalanceTestId}
                    style={{
                        color: Tokens.textPrimary,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                    }}
                >
                    {rightRunningLabel}
                </div>
            </div>
        </div>
    );
};

export default TradebookEventRow;
